import { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, ArrowRight, Archive, Link as LinkIcon, RefreshCw, Database } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function RelinkDialog({ open, onOpenChange, schedule, refType, options, onDone }) {
  const [newId, setNewId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return options;
    const needle = q.trim().toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(needle));
  }, [options, q]);

  const reset = () => { setNewId(""); setReason(""); setQ(""); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const submit = async () => {
    if (!newId) { toast.error(`Pick a replacement ${refType}`); return; }
    setBusy(true);
    try {
      await api.post("/admin/maintenance/reconcile-schedule", {
        schedule_id: schedule.id, action: "relink", ref_type: refType,
        new_id: newId, reason: reason || null,
      });
      toast.success(`Re-linked to new ${refType}`);
      reset();
      onDone();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!schedule) return null;
  const currentName = refType === "course" ? schedule.course_name
                    : refType === "teacher" ? schedule.teacher_name
                    : schedule.student_name;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl" data-testid={`relink-dialog-${refType}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-secondary" />Re-link schedule to new {refType}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border bg-muted/40 p-3 text-xs">
            <p><b>Schedule:</b> {DAYS[schedule.day_of_week] || "—"} {schedule.start_time || ""}–{schedule.end_time || ""}</p>
            <p><b>Was pointing to:</b> {currentName || <i>(no name preserved)</i>}</p>
            <p className="text-muted-foreground italic mt-1">The schedule keeps its day/time. Only the {refType} reference changes.</p>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pick replacement {refType}</label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${refType}s by name…`} className="mt-1.5" data-testid={`relink-search-${refType}`} />
            <Select value={newId} onValueChange={setNewId}>
              <SelectTrigger className="mt-2" data-testid={`relink-target-${refType}`}>
                <SelectValue placeholder={`Pick a ${refType}…`} />
              </SelectTrigger>
              <SelectContent className="max-h-[260px]">
                {filtered.slice(0, 200).map((o) => (
                  <SelectItem key={o.id} value={o.id} data-testid={`relink-option-${o.id}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reason (audit log)</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. course was renamed; re-linking to new entry" className="mt-1.5" data-testid="relink-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !newId} className="bg-secondary hover:bg-[#D96C52]" data-testid="relink-submit">
            {busy ? "Re-linking…" : "Re-link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DanglingTable({ rows, refType, options, onDone }) {
  const [relinkRow, setRelinkRow] = useState(null);
  const archive = async (row) => {
    const reason = window.prompt(`Archive this orphan schedule? Type a short reason (will be saved in the audit log):`, "dangling reference — archived");
    if (!reason) return;
    try {
      await api.post("/admin/maintenance/reconcile-schedule", {
        schedule_id: row.id, action: "archive", ref_type: refType, reason,
      });
      toast.success("Schedule archived");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (rows.length === 0) {
    return (
      <Card className="border-emerald-500/30 bg-emerald-50/40" data-testid={`empty-${refType}`}>
        <CardContent className="p-6 text-center text-sm text-emerald-700 flex flex-col items-center gap-2">
          <ShieldCheck className="h-6 w-6" />No dangling {refType} references — everything linked!
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Schedule ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Original {refType}</TableHead>
              <TableHead>Other refs</TableHead>
              <TableHead>Slot</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const orig = refType === "course" ? r.course_name
                         : refType === "teacher" ? r.teacher_name : r.student_name;
              const otherRefs = (
                <span className="text-xs text-muted-foreground">
                  {refType !== "course" && <>course: <b>{r.course_name || "—"}</b><br /></>}
                  {refType !== "teacher" && <>teacher: <b>{r.teacher_name || "—"}</b><br /></>}
                  {refType !== "student" && <>student: <b>{r.student_name || "—"}</b></>}
                </span>
              );
              return (
                <TableRow key={r.id} data-testid={`dangling-${refType}-${r.id}`}>
                  <TableCell className="font-mono text-[10px]">{r.id.slice(0, 8)}…</TableCell>
                  <TableCell className="text-xs">{r.status}</TableCell>
                  <TableCell className="text-xs"><b>{orig || <i>(no name)</i>}</b></TableCell>
                  <TableCell>{otherRefs}</TableCell>
                  <TableCell className="text-xs">{r.day_label} {r.start_time || ""}{r.end_time ? `–${r.end_time}` : ""}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setRelinkRow(r)} data-testid={`relink-btn-${r.id}`}>
                      <LinkIcon className="h-3 w-3 mr-1" />Re-link
                    </Button>
                    <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-700"
                            onClick={() => archive(r)} data-testid={`archive-btn-${r.id}`}>
                      <Archive className="h-3 w-3 mr-1" />Archive
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <RelinkDialog open={!!relinkRow} onOpenChange={(o) => !o && setRelinkRow(null)}
                    schedule={relinkRow} refType={refType} options={options}
                    onDone={() => { onDone(); setRelinkRow(null); }} />
    </>
  );
}

export default function DanglingRefsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/maintenance/dangling-schedules");
      setData(d);
      const { data: b } = await api.get("/admin/maintenance/backups");
      setBackups(b || []);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function backup() {
    setBusy(true);
    try {
      const r = await api.post("/admin/maintenance/backup");
      toast.success(`Backup created: ${r.data.file} (${r.data.size_mb} MB)`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  if (loading || !data) return <div className="p-8"><Spinner /></div>;
  const recentBackup = backups[0];
  const recentEnoughHours = recentBackup
    ? (Date.now() - new Date(recentBackup.created_at).getTime()) / 1000 / 3600 : 999;

  const nCourse = data.dangling_course.length;
  const nTeacher = data.dangling_teacher.length;
  const nStudent = data.dangling_student.length;
  const total = nCourse + nTeacher + nStudent;

  return (
    <div className="p-6 space-y-6" data-testid="dangling-refs-page">
      <PageHeader title="Reconcile Orphan Schedules"
                  subtitle="Re-point or archive class_schedule rows whose course / teacher / student references no longer exist" />

      {recentEnoughHours > 1 && (
        <Card className="border-amber-500/40 bg-amber-50" data-testid="backup-prompt">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0" />
            <div className="text-sm flex-1">
              <b>Take a backup before editing.</b>{" "}
              {recentBackup
                ? <>Last backup was {Math.round(recentEnoughHours)} hour(s) ago.</>
                : <>You have no backups yet — take one before reconciling.</>}
            </div>
            <Button onClick={backup} disabled={busy} className="bg-secondary hover:bg-[#D96C52]" data-testid="backup-prompt-btn">
              <Database className="h-4 w-4 mr-1.5" />{busy ? "Backing up…" : "Backup now"}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={load} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1.5" />Refresh
        </Button>
        <div className="text-sm text-muted-foreground self-center ml-2">
          {total === 0 ? <span className="text-emerald-700 font-semibold">🎉 No dangling refs — your data is clean</span>
                       : <span><b className="text-[#D90429]">{total}</b> dangling references found</span>}
        </div>
      </div>

      <Tabs defaultValue={nCourse > 0 ? "course" : nTeacher > 0 ? "teacher" : "student"}>
        <TabsList>
          <TabsTrigger value="course" data-testid="tab-course">Course refs ({nCourse})</TabsTrigger>
          <TabsTrigger value="teacher" data-testid="tab-teacher">Teacher refs ({nTeacher})</TabsTrigger>
          <TabsTrigger value="student" data-testid="tab-student">Student refs ({nStudent})</TabsTrigger>
        </TabsList>
        <TabsContent value="course"><DanglingTable rows={data.dangling_course} refType="course"
                          options={data.available_courses} onDone={load} /></TabsContent>
        <TabsContent value="teacher"><DanglingTable rows={data.dangling_teacher} refType="teacher"
                          options={data.available_teachers} onDone={load} /></TabsContent>
        <TabsContent value="student"><DanglingTable rows={data.dangling_student} refType="student"
                          options={data.available_students} onDone={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
